import React, { useState, useRef, useCallback, useEffect } from 'react';
import peaceTechLogo from '../assets/peace-tech-logo.png';
import peaceTech from '../assets/peace-tech.png';
import passIcon from '../assets/pass-icon.png';
import noiseIcon from '../assets/noise-icon.png';
import ccremoteIcon from '../assets/ccremote-icon.png';

const STICKERS = [
  { id: 'heron1', src: peaceTechLogo, size: 80, initX: 30, initY: 20, rotate: -8 },
  { id: 'heron2', src: peaceTech, size: 72, initX: 200, initY: 60, rotate: 12 },
  { id: 'pass', src: passIcon, size: 56, initX: 60, initY: 160, rotate: -15 },
  { id: 'noise', src: noiseIcon, size: 56, initX: 220, initY: 180, rotate: 6 },
  { id: 'ccremote', src: ccremoteIcon, size: 56, initX: 140, initY: 100, rotate: -5 },
];

const DraggableSticker = ({ sticker }) => {
  const [pos, setPos] = useState({ x: sticker.initX, y: sticker.initY });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [zIndex, setZIndex] = useState(1);
  const ref = useRef(null);

  const handleStart = useCallback((clientX, clientY) => {
    const rect = ref.current.getBoundingClientRect();
    setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    setDragging(true);
    setZIndex(Date.now());
  }, []);

  const handleMove = useCallback((clientX, clientY) => {
    if (!dragging) return;
    const parent = ref.current.parentElement.getBoundingClientRect();
    setPos({
      x: clientX - parent.left - dragOffset.x,
      y: clientY - parent.top - dragOffset.y,
    });
  }, [dragging, dragOffset]);

  const handleEnd = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      e.preventDefault();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onEnd = () => handleEnd();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [dragging, handleMove, handleEnd]);

  return (
    <div
      ref={ref}
      className={`draggable-sticker ${dragging ? 'dragging' : ''}`}
      style={{
        position: 'absolute',
        left: pos.x,
        top: pos.y,
        zIndex,
        transform: `rotate(${sticker.rotate}deg) scale(${dragging ? 1.12 : 1})`,
        width: sticker.size,
        height: sticker.size,
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        transition: dragging ? 'transform 0.1s ease' : 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseDown={(e) => { e.preventDefault(); handleStart(e.clientX, e.clientY); }}
      onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
    >
      <img
        src={sticker.src}
        alt={sticker.id}
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 14,
          boxShadow: dragging
            ? '0 12px 28px rgba(0,0,0,0.18)'
            : '0 4px 12px rgba(0,0,0,0.1)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

const DraggableStickers = () => {
  return (
    <div className="stickers-area">
      {STICKERS.map((s) => (
        <DraggableSticker key={s.id} sticker={s} />
      ))}
    </div>
  );
};

export default DraggableStickers;
